/* =========================================================
   Hover summary card.

   Rendered through a portal so the table's overflow can never
   clip it, and flipped above the trigger when there is not
   enough room below. Used by the organization, site and
   instructor name cells.
   ========================================================= */
import React, { type ReactNode } from 'react';
import { createPortal } from 'react-dom';

const OPEN_DELAY = 160;

export interface HoverCardProps {
  /** What sits in the table cell. */
  trigger: ReactNode;
  /** Card body — only rendered while open. */
  children: () => ReactNode;
  width?: number;
  height?: number;
}

export function HoverCard({ trigger, children, width = 330, height = 290 }: HoverCardProps) {
  const [open, setOpen] = React.useState(false);
  const [pos, setPos] = React.useState({ x: 0, y: 0, place: 'below' as 'below' | 'above' });
  const ref = React.useRef<HTMLDivElement>(null);
  const timer = React.useRef<number | undefined>(undefined);

  function place() {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const below = window.innerHeight - rect.bottom > height + 16;
    setPos({
      x: Math.max(12, Math.min(rect.left, window.innerWidth - width - 12)),
      y: below ? rect.bottom + 8 : rect.top - 8,
      place: below ? 'below' : 'above',
    });
  }

  React.useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <div
      ref={ref}
      onMouseEnter={() => {
        timer.current = window.setTimeout(() => {
          place();
          setOpen(true);
        }, OPEN_DELAY);
      }}
      onMouseLeave={() => {
        window.clearTimeout(timer.current);
        setOpen(false);
      }}
      style={{ display: 'inline-block' }}
    >
      {trigger}
      {open
        ? createPortal(
            <div
              role="tooltip"
              style={{
                position: 'fixed', left: pos.x, top: pos.y, width, zIndex: 2000,
                transform: pos.place === 'above' ? 'translateY(-100%)' : 'none',
                background: 'var(--surface)', border: '1px solid var(--line)', borderRadius: 12,
                boxShadow: 'var(--shadow-2)', padding: 14, pointerEvents: 'none',
                animation: 'siteCardIn .14s ease both',
              }}
            >
              {children()}
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}

/** Label / value line inside a hover card. */
export function HoverRow({ label, children, bad }: { label: ReactNode; children: ReactNode; bad?: boolean }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, fontSize: 12.5, lineHeight: 1.5 }}>
      <span style={{ flex: '0 0 96px', color: 'var(--text-3)', fontWeight: 600 }}>{label}</span>
      <span style={{ flex: 1, color: bad ? 'var(--err)' : 'var(--text)', fontWeight: bad ? 700 : 500, minWidth: 0 }}>
        {children}
      </span>
    </div>
  );
}

/** Square initials badge used as the hover card's avatar. */
export function InitialsBadge({ name, size = 38, radius = 9 }: { name: string; size?: number; radius?: number }) {
  return (
    <div
      style={{
        width: size, height: size, borderRadius: radius, flexShrink: 0,
        background: 'linear-gradient(140deg,var(--brand-primary-l),#fff)', border: '1px solid var(--line)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--brand-primary-d)',
        fontWeight: 800, fontSize: size * 0.37,
      }}
    >
      {name.split(' ').map((w) => w[0]).slice(0, 2).join('')}
    </div>
  );
}
